'use client'

import type { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider as AzureMsalProvider } from '@azure/msal-react'
import { AlertCircleIcon } from 'lucide-react'
import type React from 'react'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useToast } from '@/components/ui'
import { usePublicTenantConfig } from '@/hooks/queries/tenants'
import { createMsal } from '@/lib/msal'

export type MsalContextProps = {
  loadingMsalConfig: boolean
  hasSso: boolean
}

const MsalContext = createContext<MsalContextProps>({} as MsalContextProps)

export const MsalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { toast } = useToast()
  const { publicConfig: config, isLoadingPublicConfig: loadingMsalConfig } =
    usePublicTenantConfig()

  const [instance, setInstance] = useState<PublicClientApplication>()
  const [hasSso, setHasSso] = useState(false)

  const handleCreateMsal = useCallback(() => {
    try {
      if (!config) {
        toast({
          title: 'Falha ao carregar configurações de login pela microsoft',
          icon: AlertCircleIcon,
          variant: 'destructive',
        })
        return
      }

      const { hasSSO, msalInstance } = createMsal(config)
      setInstance(msalInstance)
      setHasSso(hasSSO)
    } catch {
      toast({
        title: 'Falha ao carregar configurações de login pela microsoft',
        icon: AlertCircleIcon,
        variant: 'destructive',
      })
    }
  }, [config, toast])

  useEffect(() => {
    handleCreateMsal()
  }, [handleCreateMsal])

  const providerValue = useMemo(
    () => ({
      loadingMsalConfig,
      hasSso,
    }),
    [hasSso, loadingMsalConfig],
  )

  if (!instance) {
    return (
      <MsalContext.Provider value={providerValue}>
        {children}
      </MsalContext.Provider>
    )
  }

  return (
    <MsalContext.Provider value={providerValue}>
      <AzureMsalProvider instance={instance}>{children}</AzureMsalProvider>
    </MsalContext.Provider>
  )
}

export const useMsal = () => {
  const context = useContext(MsalContext)

  if (!context || Object.keys(context).length === 0) {
    throw new Error('useMsal should be used within <MsalProvider>')
  }

  return context
}
